class OblivionStore {
  constructor() {
    this.routes = window.oblivionRoutes || { root: '/', cart: '/cart', search: '/search' };
    this.cartOverlay = document.querySelector('[data-cart-overlay]');
    this.searchOverlay = document.querySelector('[data-search-overlay]');
    this.mobileShopOverlay = document.querySelector('[data-mobile-shop-overlay]');
    this.currency = document.documentElement.lang === 'en' ? 'USD' : 'USD';
    this.bind();
    this.refreshCart();
    this.initReveals();
    this.restoreCabinet();
  }

  bind() {
    document.querySelectorAll('[data-cart-open]').forEach(button => button.addEventListener('click', () => this.openCart()));
    document.querySelectorAll('[data-cart-close]').forEach(button => button.addEventListener('click', () => this.closeCart()));
    document.querySelectorAll('[data-search-open]').forEach(button => button.addEventListener('click', () => this.openSearch()));
    document.querySelectorAll('[data-search-close]').forEach(button => button.addEventListener('click', () => this.closeSearch()));
    document.querySelectorAll('[data-shop-open]').forEach(button => button.addEventListener('click', () => this.openMobileShop()));
    document.querySelectorAll('[data-shop-close]').forEach(button => button.addEventListener('click', () => this.closeMobileShop()));
    document.querySelectorAll('[data-quick-add]').forEach(form => form.addEventListener('submit', event => this.addToCart(event)));
    document.querySelectorAll('[data-product-form]').forEach(form => this.initProductForm(form));
    document.querySelectorAll('[data-save-product]').forEach(button => button.addEventListener('click', () => this.toggleSave(button)));
    document.querySelectorAll('[data-filter]').forEach(button => button.addEventListener('click', () => this.filterProducts(button)));
    document.querySelector('[data-sort-products]')?.addEventListener('change', event => this.sortProducts(event.target.value));
    document.querySelector('[data-predictive-search]')?.addEventListener('input', event => this.predictiveSearch(event.target.value));
    document.querySelectorAll('[data-search-prompt]').forEach(button => button.addEventListener('click', () => {
      const input = document.querySelector('[data-predictive-search]');
      input.value = button.dataset.searchPrompt;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.focus();
    }));
    document.addEventListener('keydown', event => {
      if (event.key === 'Escape') { this.closeCart(); this.closeSearch(); this.closeMobileShop(); }
    });
  }

  money(cents) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: this.currency, maximumFractionDigits: 2 }).format(cents / 100);
  }

  async addToCart(event) {
    event.preventDefault();
    const form = event.currentTarget;
    const button = form.querySelector('button[type="submit"]');
    button.disabled = true;
    try {
      const formData = new FormData(form);
      if (form.dataset.currentVariantId) formData.set('id', form.dataset.currentVariantId);
      const response = await fetch(`${this.routes.root}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: formData });
      if (!response.ok) throw new Error('Unable to add this relic.');
      button.textContent = button.dataset.purchaseState === 'preorder' ? 'Preorder added ✓' : 'Added to bag ✓';
      await this.refreshCart();
      this.openCart();
    } catch (error) {
      button.textContent = error.message;
    } finally {
      button.disabled = button.dataset.purchaseState === 'sold-out';
    }
  }

  initProductForm(form) {
    const configurator = form.querySelector('[data-variant-configurator]');
    if (!configurator) return;
    let variants;
    try {
      variants = JSON.parse(configurator.dataset.variants || '[]');
      const overflowVariants = JSON.parse(configurator.dataset.overflowVariants || '[]');
      if (Array.isArray(overflowVariants)) variants.push(...overflowVariants);
    } catch {
      return;
    }
    const groups = [...configurator.querySelectorAll('[data-option-group]')];
    if (!variants.length || !groups.length) return;
    const variantInput = form.querySelector('[data-variant-id]');
    const button = form.querySelector('[data-purchase-button]');
    const price = form.closest('.main-product')?.querySelector('[data-product-price]');
    const preorderNotice = form.querySelector('[data-preorder-notice]');
    const preorderProperties = [...form.querySelectorAll('[data-preorder-property]')];
    const dynamicCheckout = form.querySelector('[data-dynamic-checkout]');

    const updateVariant = variant => {
      const { id, state, price: formattedPrice } = variant;
      variantInput.value = String(id);
      variantInput.setAttribute('value', String(id));
      form.dataset.currentVariantId = String(id);
      button.dataset.purchaseState = state;
      button.disabled = state === 'sold-out';
      button.innerHTML = state === 'in-stock'
        ? `Add to bag <span class="purchase-button-price">${formattedPrice}</span>`
        : state === 'preorder'
          ? `Preorder <span class="purchase-button-price">${formattedPrice}</span>`
          : 'Sold out';
      if (price) price.textContent = formattedPrice;
      if (preorderNotice) preorderNotice.hidden = state !== 'preorder';
      preorderProperties.forEach(input => { input.disabled = state !== 'preorder'; });
      if (dynamicCheckout) dynamicCheckout.hidden = state !== 'in-stock';
      const url = new URL(window.location.href);
      url.searchParams.set('variant', id);
      window.history.replaceState({}, '', url);
    };

    const selectedValues = () => groups.map(group => group.querySelector('[data-option-button][aria-pressed="true"]')?.dataset.optionValue || '');

    const valueIsAvailable = (index, value) => variants.some(variant =>
      variant.options.slice(0, index).every((option, optionIndex) => option === selectedValues()[optionIndex])
      && variant.options[index] === value
    );

    const valueState = (index, value) => {
      const matching = variants.filter(variant =>
        variant.options.slice(0, index).every((option, optionIndex) => option === selectedValues()[optionIndex])
        && variant.options[index] === value
      );
      if (matching.some(variant => variant.state === 'in-stock')) return 'in-stock';
      if (matching.some(variant => variant.state === 'preorder')) return 'preorder';
      return 'sold-out';
    };

    const selectValue = (group, value) => {
      group.querySelectorAll('[data-option-button]').forEach(button => {
        button.setAttribute('aria-pressed', String(button.dataset.optionValue === value));
      });
      const label = group.querySelector('[data-option-selection]');
      if (label) label.textContent = value;
    };

    const doubletteGaugeGroup = configurator.querySelector('[data-doublette-gauge-group]');
    const doubletteLengthGroup = configurator.querySelector('[data-doublette-length-group]');
    const doubletteSizeGroup = groups.find(group => group.dataset.optionIndex === '1');
    const doubletteGaugeButtons = doubletteGaugeGroup ? [...doubletteGaugeGroup.querySelectorAll('[data-doublette-gauge-button]')] : [];
    const doubletteLengthButtons = doubletteLengthGroup ? [...doubletteLengthGroup.querySelectorAll('[data-doublette-length-button]')] : [];

    const doubletteSizeState = (gauge, length) => {
      const selected = selectedValues();
      const matching = variants.filter(variant =>
        variant.options[0] === selected[0]
        && variant.options[1] === `${gauge} · ${length}`
      );
      if (matching.some(variant => variant.state === 'in-stock')) return 'in-stock';
      if (matching.some(variant => variant.state === 'preorder')) return 'preorder';
      return 'sold-out';
    };

    const reconcileDoubletteControls = () => {
      if (!doubletteSizeGroup || !doubletteGaugeGroup || !doubletteLengthGroup) return;
      const [selectedGauge = '', selectedLength = ''] = (selectedValues()[1] || '').split(' · ');
      const gaugeLabel = doubletteGaugeGroup.querySelector('[data-doublette-gauge-selection]');
      const lengthLabel = doubletteLengthGroup.querySelector('[data-doublette-length-selection]');
      if (gaugeLabel) gaugeLabel.textContent = selectedGauge;
      if (lengthLabel) lengthLabel.textContent = selectedLength;
      doubletteGaugeButtons.forEach(choice => {
        const state = doubletteSizeState(choice.dataset.doubletteGauge, selectedLength);
        choice.setAttribute('aria-pressed', String(choice.dataset.doubletteGauge === selectedGauge));
        choice.disabled = state === 'sold-out';
        choice.dataset.variantState = state;
      });
      doubletteLengthButtons.forEach(choice => {
        const state = doubletteSizeState(selectedGauge, choice.dataset.doubletteLength);
        choice.setAttribute('aria-pressed', String(choice.dataset.doubletteLength === selectedLength));
        choice.disabled = state === 'sold-out';
        choice.dataset.variantState = state;
      });
    };

    const reconcile = () => {
      groups.forEach((group, index) => {
        const buttons = [...group.querySelectorAll('[data-option-button]')];
        buttons.forEach(button => {
          const state = valueState(index, button.dataset.optionValue);
          button.disabled = state === 'sold-out';
          button.dataset.variantState = state;
        });
        const selected = group.querySelector('[data-option-button][aria-pressed="true"]');
        if (selected?.disabled) selectValue(group, buttons.find(button => !button.disabled)?.dataset.optionValue || '');
      });
      const selected = selectedValues();
      const variant = variants.find(candidate => candidate.options.every((option, index) => option === selected[index]));
      if (variant) updateVariant(variant);
      reconcileDoubletteControls();
    };

    configurator.querySelectorAll('[data-option-button]').forEach(button => button.addEventListener('click', () => {
      if (button.disabled) return;
      selectValue(groups[Number(button.dataset.optionIndex)], button.dataset.optionValue);
      reconcile();
    }));
    doubletteGaugeButtons.forEach(choice => choice.addEventListener('click', () => {
      if (choice.disabled || !doubletteSizeGroup) return;
      const [, selectedLength = ''] = (selectedValues()[1] || '').split(' · ');
      selectValue(doubletteSizeGroup, `${choice.dataset.doubletteGauge} · ${selectedLength}`);
      reconcile();
    }));
    doubletteLengthButtons.forEach(choice => choice.addEventListener('click', () => {
      if (choice.disabled || !doubletteSizeGroup) return;
      const [selectedGauge = ''] = (selectedValues()[1] || '').split(' · ');
      selectValue(doubletteSizeGroup, `${selectedGauge} · ${choice.dataset.doubletteLength}`);
      reconcile();
    }));
    reconcile();
  }

  async refreshCart() {
    if (!this.cartOverlay) return;
    const response = await fetch(`${this.routes.root}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) return;
    const cart = await response.json();
    document.querySelectorAll('[data-cart-count]').forEach(el => { el.textContent = cart.item_count; });
    const items = this.cartOverlay.querySelector('[data-cart-items]');
    items.replaceChildren();
    cart.items.forEach(item => {
      const row = document.createElement('div');
      row.className = 'cart-item';
      const detail = document.createElement('div');
      const title = document.createElement('a');
      title.href = item.url;
      title.textContent = item.product_title;
      detail.append(title);
      if (item.properties?.['Purchase type'] === 'Preorder') {
        const preorder = document.createElement('p');
        preorder.className = 'cart-item-preorder';
        preorder.textContent = `Preorder · ${item.properties['Estimated fulfillment']}`;
        detail.append(preorder);
      }
      const price = document.createElement('span');
      price.textContent = this.money(item.final_line_price);
      row.append(detail, price);
      items.append(row);
    });
    this.cartOverlay.querySelector('[data-cart-empty]').hidden = cart.item_count > 0;
    this.cartOverlay.querySelector('[data-cart-summary]').hidden = cart.item_count === 0;
    this.cartOverlay.querySelector('[data-cart-total]').textContent = this.money(cart.total_price);
  }

  openCart() { if (this.cartOverlay) { this.closeMobileShop(); this.cartOverlay.hidden = false; document.body.classList.add('drawer-open'); } }
  closeCart() { if (this.cartOverlay) { this.cartOverlay.hidden = true; document.body.classList.remove('drawer-open'); } }
  openSearch() { if (this.searchOverlay) { this.closeMobileShop(); this.searchOverlay.hidden = false; document.body.classList.add('drawer-open'); setTimeout(() => this.searchOverlay.querySelector('input')?.focus(), 50); } }
  closeSearch() { if (this.searchOverlay) { this.searchOverlay.hidden = true; document.body.classList.remove('drawer-open'); } }
  openMobileShop() { if (this.mobileShopOverlay) { this.mobileShopOverlay.hidden = false; document.body.classList.add('drawer-open'); this.mobileShopOverlay.querySelector('a')?.focus(); } }
  closeMobileShop() { if (this.mobileShopOverlay) { this.mobileShopOverlay.hidden = true; document.body.classList.remove('drawer-open'); } }

  toggleSave(button) {
    const saved = new Set(JSON.parse(localStorage.getItem('oblivion-cabinet') || '[]'));
    const id = button.dataset.saveProduct;
    saved.has(id) ? saved.delete(id) : saved.add(id);
    localStorage.setItem('oblivion-cabinet', JSON.stringify([...saved]));
    this.renderCabinet(saved);
  }
  restoreCabinet() { this.renderCabinet(new Set(JSON.parse(localStorage.getItem('oblivion-cabinet') || '[]'))); }
  renderCabinet(saved) {
    document.querySelectorAll('[data-save-product]').forEach(button => { const active = saved.has(button.dataset.saveProduct); button.classList.toggle('saved', active); button.textContent = active ? '×' : '◇'; });
    document.querySelectorAll('[data-cabinet-count]').forEach(el => { el.textContent = saved.size; });
  }

  filterProducts(button) {
    const filter = button.dataset.filter;
    document.querySelectorAll('[data-filter]').forEach(chip => { const active = chip === button; chip.classList.toggle('active', active); chip.setAttribute('aria-pressed', String(active)); });
    let count = 0;
    document.querySelectorAll('[data-product-card]').forEach(card => { const visible = filter === 'all' || card.dataset.category === filter; card.hidden = !visible; if (visible) count += 1; });
    const note = document.querySelector('[data-results-count]');
    if (note) note.textContent = `Showing ${count} ${count === 1 ? 'relic' : 'relics'}`;
  }
  sortProducts(mode) {
    const grid = document.querySelector('[data-product-grid]');
    if (!grid) return;
    const cards = [...grid.querySelectorAll('[data-product-card]')];
    cards.sort((a, b) => mode === 'high' ? Number(b.dataset.price) - Number(a.dataset.price) : mode === 'low' ? Number(a.dataset.price) - Number(b.dataset.price) : 0).forEach(card => grid.append(card));
  }

  async predictiveSearch(query) {
    const output = document.querySelector('[data-predictive-results]');
    if (!output) return;
    if (query.trim().length < 2) { output.replaceChildren(); return; }
    const url = `${this.routes.root}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,collection,page,article&resources[limit]=6`;
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) return;
    const data = await response.json();
    const resources = data.resources?.results || {};
    const results = [...(resources.products || []), ...(resources.collections || []), ...(resources.pages || []), ...(resources.articles || [])];
    output.replaceChildren();
    results.forEach(result => {
      const link = document.createElement('a');
      link.href = result.url;
      const type = document.createElement('span');
      type.textContent = result.type || 'Archive result';
      const title = document.createElement('strong');
      title.textContent = result.title;
      link.append(type, title);
      output.append(link);
    });
    if (!results.length) output.textContent = 'No relic found. Try a material, placement, or service.';
  }

  initReveals() {
    const targets = document.querySelectorAll('[data-reveal]');
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { targets.forEach(el => el.classList.add('revealed')); return; }
    const observer = new IntersectionObserver(entries => entries.forEach(entry => { if (entry.isIntersecting) entry.target.classList.add('revealed'); }), { threshold: .1 });
    targets.forEach(el => observer.observe(el));
  }
}

document.addEventListener('DOMContentLoaded', () => new OblivionStore());
